/* eslint-disable no-unused-vars */
/* eslint-disable import/no-unresolved */
import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Decimal from 'decimal.js';
import { useD3 } from '../../hooks/useD3';
import d3 from '../../utils/d3Importer';
import slices from '../../state/slices/data';

const blue = '#243f6f';
const dark = '#2b2b2b';
const orange = '#f06f10';
const red = '#e12827';

export default function Chartbar() {
  const dispatch = useDispatch();
  const curData = useSelector((state) => state.data.curData);
  const earnLess = useSelector((state) => state.data.earnLess);
  const earnMore = useSelector((state) => state.data.earnMore);
  const incomeSelectActive = useSelector(
    (state) => state.data.incomeSelectActive,
  );
  const incomeLevel = useSelector((state) => state.data.incomeLevel);
  const timeFrame = useSelector((state) => state.data.timeframe);

  const { setIncome } = slices.actions;

  const stageWidth = window.innerWidth > 630 ? 630 : window.innerWidth;
  const margin = { top: 50, right: 70, bottom: 50, left: 70 };
  const height = window.innerHeight < 450 ? stageWidth : stageWidth * 0.75;

  function roundto1dp(num) {
    return Number(new Decimal(num).toFixed(1));
  }

  const refBar = useD3((svg) => {
    if (curData.length > 0) {
      //
      d3.select('.bar-chart-b').html('');
      d3.select('.x-axis-b').html('');
      d3.select('.y-axis-b').html('');
      d3.select('.x-axis-c').html('');
      d3.select('.invis-area-b').html('');
      d3.select('.text-hint-b').html('');
      d3.select('.indicator-bar-b').html('');
      d3.select('.defs').html('');

      // Set up the SVG
      const chartWidth = stageWidth - margin.left - margin.right;
      const chartHeight = height - margin.top - margin.bottom;

      const x = d3
        .scaleBand()
        .domain(curData.map((d) => d.iID))
        .range([0, chartWidth])
        .paddingInner(0)
        .paddingOuter(0);

      // const xDisplay = d3.scaleLinear().domain([0, 230]).range([0, chartWidth]);

      const y = d3.scaleLinear().domain([0, 10]).range([chartHeight, 0]);
      const y2 = d3.scaleLinear().domain([0, 10]).range([0, chartHeight]);

      const xAxis = (g) => {
        g.attr('transform', `translate(0,${chartHeight})`).call(
          d3.axisBottom(x),
        );
      };

      const yAxis = (g) => {
        g.call(
          d3
            .axisLeft(y)
            .ticks(5)
            .tickSize(-chartWidth)
            .tickFormat((d) => `${d}%`),
        ).call(() => {
          g.selectAll('line').attr('stroke-dasharray', (d, i) => {
            return '2,4';
          });
          g.selectAll('line').style('opacity', (d, i) => {
            return i === 0 ? 0 : 0.5;
          });
        });
      };

      // draw axes
      svg
        .select('.chart-b')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);

      // patterns
      const defs = svg
        .append('defs')
        .attr('class', 'defs')
        .append('pattern')
        .attr('id', 'pattern-plain')
        .attr('width', 800)
        .attr('height', 536)
        .attr('patternUnits', 'userSpaceOnUse')
        .append('image')
        .attr('xlink:href', 'images/texturePlain.jpg');

      const defs2 = svg
        .select('.defs')
        .append('pattern')
        .attr('id', 'pattern-blue')
        .attr('width', 800)
        .attr('height', 536)
        .attr('patternUnits', 'userSpaceOnUse')
        .append('image')
        .attr('xlink:href', 'images/textureBlue.jpg');

      svg.select('.y-axis-b').call(yAxis);
      svg.select('.x-axis-b').call(xAxis);

      // draw $'s
      const tickValues = [
        {
          value: 'level0',
          labelAnnual: '$0',
          value2: 'level0',
          labelAnnual2: '$0',
        },
        {
          value: 'level9',
          labelAnnual: '$50k',
          value2: 'level10',
          labelAnnual2: '$1,000',
        },
        {
          value: 'level19',
          labelAnnual: '$100k',
          value2: 'level20',
          labelAnnual2: '$2,000',
        },
        {
          value: 'level30',
          labelAnnual: '$150k',
          value2: 'level30',
          labelAnnual2: '$3,000',
        },
        {
          value: 'level42',
          labelAnnual: '$200k',
          value2: 'level40',
          labelAnnual2: '$4,000',
        },
      ];
      tickValues.forEach((d, i) => {
        svg
          .select('.x-axis-c')
          .append('text')
          .text(() => {
            return timeFrame === 'annual' ? d.labelAnnual : d.labelAnnual2;
          })
          .attr('x', () => {
            const key = timeFrame === 'annual' ? 'value' : 'value2';
            return x(d[key]) + x.bandwidth() / 2;
          })
          .attr('y', chartHeight + 20)
          .attr('fill', '#777')
          .attr('font-size', '11px')
          .attr('text-anchor', 'middle');
      });

      // draw bars
      svg
        .select('.bar-chart-b')
        .selectAll('g')
        .data(curData)
        .join('g')
        .attr('fill', '#fff')
        .selectAll('rect')
        .data((d, i) => {
          return [{ ...d, i }];
        })
        .join('rect')
        .attr('class', (d, i) => {
          return `bar bar${d.i}`;
        })
        .attr('x', (d) => {
          return x(d.iID);
        })
        .attr('y', (d) => {
          return chartHeight - Math.abs(y2(d.diff));
        })
        .attr('height', (d) => Math.abs(y2(d.diff)))
        .attr('width', x.bandwidth())
        .style('fill', 'url("#pattern-plain")')
        .style('stroke', '#bbb')
        .style('stroke-width', 1.2)
        .style('opacity', 1);

      if (incomeSelectActive) {
        console.log('incomeSelectActive ', incomeLevel);
        const indicatorIndex = curData.findIndex((d) => d.iID === incomeLevel);
        const curObject = curData[indicatorIndex];
        svg
          .select('.indicator-bar-b')
          .append('rect')
          .attr('x', (d) => {
            return x(curObject.iID);
          })
          .attr('y', (d) => {
            return chartHeight - Math.abs(y2(curObject.diff));
          })
          .attr('height', (d) => Math.abs(y2(curObject.diff)))
          .attr('width', x.bandwidth())
          .style('fill', 'url("#pattern-blue")')
          .style('stroke', blue)
          .style('stroke-width', 1.5)
          .style('opacity', 1);

        const triangleCenterX = x(curObject.iID) + x.bandwidth() / 2;
        const triangleTopY = chartHeight - y2(curObject.diff) - 10; // Adjust Y to place the triangle above the bar

        // Define the points of the triangle (top, and two base points)
        const trianglePoints = [
          [triangleCenterX, triangleTopY], // Top point
          [triangleCenterX - 6, triangleTopY - 12], // Left base point
          [triangleCenterX + 6, triangleTopY - 12], // Right base point
        ]
          .map((point) => point.join(','))
          .join(' ');

        // Append the triangle to the SVG container
        svg
          .select('.indicator-bar-b')
          .append('polygon')
          .attr('points', trianglePoints)
          .style('fill', blue); // Set the fill color of the triangle

        svg
          .select('.indicator-bar-b')
          .append('text')
          .attr('x', triangleCenterX)
          .attr('y', triangleTopY - 20)
          .attr('class', 'you')
          .attr('text-anchor', 'middle')
          .style('fill', blue)
          .text('YOU');

        if (curObject.diff < 9) {
          svg
            .select('.indicator-bar-b')
            .append('image')
            .attr('xlink:href', 'images/headIcon.svg')
            .attr('width', 20)
            .attr('height', 20)
            .attr('x', triangleCenterX - 10)
            .attr('y', triangleTopY - 58);
        }
      }

      // add invisible area for mouseover
      svg
        .select('.invis-area-b')
        .selectAll('rect')
        .data(curData)
        .join('rect')
        .attr('x', (d) => x(d.iID))
        .attr('y', 0)
        .attr('width', x.bandwidth())
        .attr('height', chartHeight)
        .attr('opacity', 0)
        .on('mousedown', (event, d, i) => {
          console.log('clicked iID:', d.iID, 'datum:', d);
          dispatch(setIncome(d.iID));
        });
    }
  });

  const style = {
    width: stageWidth,
    height,
    marginRight: '0px',
    marginLeft: '0px',
  };

  const wrapStyle = {
    display: curData.length > 0 ? 'block' : 'none',
  };

  return (
    <section className="chart-wrap" style={wrapStyle}>
      <h3>Distribution of employee earnings</h3>
      <svg ref={refBar} style={style}>
        <g className="chart-b">
          <g className="bar-chart-b" />
          <g className="x-axis-b" />
          <g className="x-axis-c" />
          <g className="y-axis-b" />
          <g className="indicator-bar-b" />
          <g className="text-hint-b" />
          <g className="invis-area-b" />
        </g>
      </svg>
    </section>
  );
}
